import React from 'react';
import { motion } from 'framer-motion';
import { DropletIcon, WrenchIcon, BarChart3Icon, MapPinIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { WATER_PRODUCTION_IMAGES as IMG, productionCopy as c, researchBenchmarks, casselmanProof, type Bi } from '../../data/waterProduction';
import { SolutionLaptop } from './solution/SolutionLaptop';
import { ProductionCapabilities } from './production/ProductionCapabilities';
const EASE = [0.23, 1, 0.32, 1] as const;
const RESEARCH_ICONS = {
  leak: DropletIcon,
  repair: WrenchIcon,
  usage: BarChart3Icon
} as const;

// Crop of the supplied image (1774 × 887): the Casselman water tower, x 1350–1774, y 0–500.
function TowerCrop({
  alt,
  className = ''
}: {
  alt: string;
  className?: string;
}) {
  return <div className={`relative overflow-hidden aspect-[424/500] ${className}`} data-id="element-7685" data-mp="3pzs83jdyp12b">
      <img src={IMG.casselman} alt={alt} className="absolute top-0 max-w-none" style={{
      width: `${1774 / 424 * 100}%`,
      left: `-${1350 / 424 * 100}%`
    }} data-id="element-7686" data-mp="3pxpkt7471m1a" />
    </div>;
}
function ResearchCard({
  k,
  value,
  text,
  tag
}: {
  k: keyof typeof RESEARCH_ICONS;
  value: string;
  text: string;
  tag: string;
}) {
  const Icon = RESEARCH_ICONS[k];
  return <div className="h-full rounded-2xl border border-[#ECECF3] bg-white p-4 flex items-center gap-4 shadow-[0_18px_40px_-28px_rgba(30,30,80,0.35)]" data-id="element-7687" data-mp="2y3ap3hmv57go">
      <span className="w-14 h-14 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7688" data-mp="3ps41t5e242li">
        <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.8} data-id="element-7689" data-mp="3uq5mfur4s9z3" />
      </span>
      <span className="min-w-0" data-id="element-7690" data-mp="3h0vdyopmo61a">
        <span className="block font-sans text-[9.5px] font-semibold uppercase tracking-[0.14em] text-[#8A90A2]" data-id="element-7691" data-mp="3gztrlo35vx49">{tag}</span>
        <span className="block font-sans text-[24px] font-semibold text-[#121A3A] leading-tight whitespace-nowrap" data-id="element-7692" data-mp="3kzaymly8l2bj">{value}</span>
        <span className="block font-sans text-[13px] leading-snug text-[#5B6478]" data-id="element-7693" data-mp="hd3xw85d8aui">{text}</span>
      </span>
    </div>;
}
function ProofCard({
  pick
}: {
  pick: (b: Bi) => string;
}) {
  return <div className="rounded-2xl border border-[#ECECF3] bg-white p-5 flex gap-3.5 shadow-[0_18px_40px_-28px_rgba(30,30,80,0.4)]" data-id="element-7694" data-mp="n6ft6bw3rzzi">
      <span className="w-11 h-11 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-7695" data-mp="i6kzy1ouuyba">
        <MapPinIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.9} data-id="element-7696" data-mp="d8jg3ejluuyr" />
      </span>
      <span data-id="element-7697" data-mp="8b9kfh5mhn2h">
        <span className="block font-sans text-[18px] font-semibold text-[#121A3A] leading-tight" data-id="element-7698" data-mp="8bt3x2ax8jim">{pick(casselmanProof.name)}</span>
        <span className="block font-sans text-[18px] font-semibold text-[#121A3A] leading-tight" data-id="element-7699" data-mp="3vmj0ggjazmnb">{pick(casselmanProof.region)}</span>
        <span className="block font-sans text-[13.5px] leading-snug text-[#4F46E5] mt-1.5" data-id="element-7700" data-mp="3qoqx8qd7ke3t">{pick(casselmanProof.text)}</span>
      </span>
    </div>;
}
export function WaterProduction() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (b: Bi) => isFR ? b.fr : b.en;
  const reveal = {
    initial: {
      opacity: 0,
      y: 8
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-60px'
    }
  };
  const message = <>
      <p className="font-sans text-[13px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-7701" data-mp="3gjrb5djfxx1t">{pick(c.eyebrow)}</p>
      <h2 className="font-serif text-[3rem] sm:text-[4rem] 2xl:text-[5.2rem] leading-[1] tracking-tight mt-7" data-id="element-7702" data-mp="3ljdqbsitxnd9">
        <span className="block text-[#0E1330]" data-id="element-7703" data-mp="2nvv71nrjl2v0">{pick(c.navy)}</span>
        <span className="block text-[#4F46E5]" data-id="element-7704" data-mp="2su6h9zlr1fdr">{pick(c.indigo)}</span>
      </h2>
      <p className="font-sans text-[17px] 2xl:text-[19px] leading-[1.6] text-[#4A5470] mt-7 max-w-[520px]" data-id="element-7705" data-mp="2ss2fbn7euqg4">
        <strong className="font-semibold text-[#2A3350]" data-id="element-7706" data-mp="2xrexvvr91bcw">{pick(c.bodyStrong1)}</strong>
        {pick(c.bodyMid)}
        <strong className="font-semibold text-[#2A3350]" data-id="element-7707" data-mp="254lz3ixf6m5j">{pick(c.bodyStrong2)}</strong>
        {pick(c.bodyEnd)}
      </p>
      <span className="block w-16 h-[3px] bg-[#4F46E5] mt-8" aria-hidden="true" data-id="element-7708" data-mp="2a2vsbnzl3coi" />
    </>;
  const research = researchBenchmarks.map(r => <ResearchCard key={r.key} k={r.key} value={pick(r.value)} text={pick(r.text)} tag={pick(c.researchTag)} data-id="element-7709" data-mp="fnqbfepn3q9l" />);
  return <div className="relative" data-id="element-7710" data-mp="pd9wmr0trw7m">
      {/* Desktop */}
      <div className="hidden xl:block relative min-h-[640px]" data-id="element-7711" data-mp="peuqeefbfb4f">
        {/* Casselman tower, right */}
        <div className="absolute top-0 right-0 w-[32%]" style={{
        WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 22%)',
        maskImage: 'linear-gradient(90deg, transparent 0%, #000 22%)'
      }} data-id="element-7712" data-mp="kkyxhqdwseg4">
          <TowerCrop alt={pick(c.towerAlt)} data-id="element-7713" data-mp="ubtgmlvuyxxc" />
        </div>
        {/* Soft lavender wash toward the centre */}
        <div className="absolute inset-y-0 left-[28%] right-[30%] bg-[#F4F4FB]/70" aria-hidden="true" data-id="element-7714" data-mp="18bcnzcgui58l" />

        <div className="relative max-w-[1760px] mx-auto min-h-[640px]" data-id="element-7715" data-mp="13ayfjtbc0ta1">
          <motion.ul {...reveal} transition={{
          duration: 0.3,
          delay: 0.1,
          ease: EASE
        }} className="absolute z-20 top-[56px] left-[29%] right-[22%] grid grid-cols-3 gap-4" data-id="element-7716" data-mp="3a4ag162sstsh">
            {research.map((r, i) => <li key={i} data-id="element-7717" data-mp="1kn5ci73ot74k" data-mpi={i}>{r}</li>)}
          </motion.ul>

          <motion.div {...reveal} transition={{
          duration: 0.3,
          delay: 0.05,
          ease: EASE
        }} className="absolute z-10 top-[200px] left-[34%] w-[38%] max-w-[680px]" data-id="element-7718" data-mp="1pkf0rjfcbcqt">
            <SolutionLaptop isFR={isFR} showActions={false} data-id="element-7719" data-mp="1fvn97ce6yugf" />
          </motion.div>

          <motion.div {...reveal} transition={{
          duration: 0.3,
          delay: 0.15,
          ease: EASE
        }} className="absolute z-20 top-[290px] right-[1.5%] w-[250px]" data-id="element-7720" data-mp="1ftam6ktkttwi">
            <ProofCard pick={pick} data-id="element-7721" data-mp="16w0ch3weqctp" />
          </motion.div>

          <motion.div {...reveal} transition={{
          duration: 0.3,
          ease: EASE
        }} className="relative z-20 w-[31%] pl-6 2xl:pl-12 pt-24 pb-40" data-id="element-7722" data-mp="rxptov5s69y3">
            {message}
          </motion.div>
        </div>

        <svg className="absolute bottom-0 left-0 w-full h-[140px] pointer-events-none z-[12]" viewBox="0 0 1440 140" preserveAspectRatio="none" aria-hidden="true" data-id="element-7723" data-mp="2mbpjbvfj4f8y">
          <path d="M0 140 L0 50 C 280 20, 520 100, 860 88 C 1120 78, 1300 40, 1440 6 L1440 140 Z" fill="#FFFFFF" data-id="element-7724" data-mp="2ra0uqalh2xta" />
        </svg>
      </div>

      {/* Tablet / mobile */}
      <div className="xl:hidden px-6 max-w-[1100px] mx-auto" data-id="element-7725" data-mp="2ktijhk8yihvd">
        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-7726" data-mp="2fvgyuuvvuahs">
          {message}
        </motion.div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-[1fr_1.1fr] gap-4 items-center" data-id="element-7727" data-mp="2budkydxeaoj7">
          <TowerCrop alt={pick(c.towerAlt)} className="rounded-2xl max-h-[420px] md:max-h-none" data-id="element-7728" data-mp="38ko0i2qmvg79" />
          <ProofCard pick={pick} data-id="element-7729" data-mp="33kjho62969dv" />
        </div>
        {/* Tablet: research before dashboard */}
        <ul className="hidden md:grid mt-6 grid-cols-3 gap-3" data-id="element-7730" data-mp="2yttcotxqo8jp">
          {research.map((r, i) => <li key={i} data-id="element-7731" data-mp="2ut9fxrh2v6y3" data-mpi={i}>{r}</li>)}
        </ul>
        <div className="mt-8" data-id="element-7732" data-mp="2uqn9jv5xaxhg">
          <SolutionLaptop isFR={isFR} showActions={false} data-id="element-7733" data-mp="3radu4mintvxl" />
        </div>
      </div>

      <div className="relative z-30 pt-14 xl:-mt-6" data-id="element-7734" data-mp="3rcgqdoaeolar">
        <ProductionCapabilities isFR={isFR} data-id="element-7735" data-mp="3mj46tskl34ov" />
      </div>

      {/* Mobile: research after capabilities */}
      <ul className="md:hidden mt-12 px-6 grid grid-cols-1 gap-3" data-id="element-7736" data-mp="192uc17y0dowj">
        {research.map((r, i) => <li key={i} data-id="element-7737" data-mp="1e0w42idewyk5" data-mpi={i}>{r}</li>)}
      </ul>

      <p className="max-w-[1720px] mx-auto px-6 mt-10 font-sans text-[12px] leading-relaxed text-[#8A90A2]" data-id="element-7738" data-mp="1i1g3jslgke9g">{pick(c.disclaimer)}</p>
    </div>;
}